import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  addDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { BidOutcome, HarvestCandidate, UserProfile, Answer, DraftSection, SmeTask } from "../types";
import { sanitizeClient } from "../utils/sanitizeClient";
import { calculateTextSimilarity } from "../utils/textSimilarity";
import { sanitizeForFirestore } from "../utils/firestoreUtils";

/**
 * Adds +90 days (or review cadence) to today formatted as YYYY-MM-DD
 */
export function calculateReviewDueDate(cadenceDays = 90): string {
  const d = new Date();
  d.setDate(d.getDate() + cadenceDays);
  return d.toISOString().split("T")[0] || "";
}

/**
 * Stamps win credit on all Answer Library items that actually informed the winning draft
 */
export async function stampWinCredit(opportunityId: string): Promise<string[]> {
  const sectionsRef = collection(db, "opportunities", opportunityId, "draft_sections");
  const snap = await getDocs(sectionsRef);

  const usedAnswerIds = new Set<string>();
  snap.docs.forEach((d) => {
    const data = d.data() as Partial<DraftSection>;
    const ids =
      Array.isArray(data.usedAnswerIds) && data.usedAnswerIds.length > 0 ? data.usedAnswerIds : [];
    ids.forEach((id) => usedAnswerIds.add(id));
  });

  const credited: string[] = [];
  const now = new Date().toISOString();

  for (const answerId of usedAnswerIds) {
    const answerRef = doc(db, "answers", answerId);
    const ansSnap = await getDoc(answerRef);
    if (ansSnap.exists()) {
      const data = ansSnap.data() as Partial<Answer>;
      const existingWins = new Set<string>(data.wonBidIds || []);
      if (!existingWins.has(opportunityId)) {
        existingWins.add(opportunityId);
        await updateDoc(answerRef, {
          wonBidIds: Array.from(existingWins),
          updatedAt: now,
        });
      }
      credited.push(answerId);
    }
  }

  // Update opportunity postMortem record if it exists
  const oppRef = doc(db, "opportunities", opportunityId);
  const oppSnap = await getDoc(oppRef);
  if (oppSnap.exists()) {
    const oppData = oppSnap.data();
    if (oppData.postMortem) {
      await updateDoc(oppRef, {
        "postMortem.creditedAnswerIds": credited,
        updatedAt: now,
      });
    }
  }

  return credited;
}

/**
 * Stamps sourceOutcome on all answers harvested from this opportunity
 */
export async function stampHarvestOutcome(
  opportunityId: string,
  outcome: BidOutcome,
): Promise<number> {
  const answersRef = collection(db, "answers");
  const q = query(answersRef, where("sourceOpportunityId", "==", opportunityId));
  const snap = await getDocs(q);

  const now = new Date().toISOString();
  let updatedCount = 0;

  for (const d of snap.docs) {
    await updateDoc(doc(db, "answers", d.id), {
      sourceOutcome: outcome,
      updatedAt: now,
    });
    updatedCount++;
  }

  return updatedCount;
}

/**
 * Scans an opportunity's drafted sections and answered SME tasks,
 * sanitizes client-specific mentions, and checks novelty against the Answer Library.
 */
export async function buildHarvestCandidates(
  opportunityId: string,
  clientName: string,
): Promise<HarvestCandidate[]> {
  // 1. Fetch existing library answers for novelty comparison
  const answersSnap = await getDocs(collection(db, "answers"));
  const existingAnswers = answersSnap.docs
    .map((d) => ({
      id: d.id,
      title: String(d.data().title || ""),
      body: String(d.data().body || ""),
      status: String(d.data().status || "active"),
    }))
    .filter((a) => a.status !== "retired");

  const candidates: HarvestCandidate[] = [];

  // Helper to find highest similarity in library
  const findBestLibraryMatch = (text: string) => {
    let bestScore = 0;
    let bestMatch: { id: string; title: string } | null = null;

    for (const ans of existingAnswers) {
      const sim = calculateTextSimilarity(text, ans.body);
      if (sim > bestScore) {
        bestScore = sim;
        bestMatch = { id: ans.id, title: ans.title };
      }
    }

    return { bestScore, bestMatch };
  };

  // 2. Fetch draft sections
  const sectionsSnap = await getDocs(
    collection(db, "opportunities", opportunityId, "draft_sections"),
  );
  sectionsSnap.docs.forEach((d) => {
    const data = d.data() as Partial<DraftSection>;
    const rawContent = (data.content || "").trim();
    if (!rawContent || rawContent.length < 50) return;

    const sanitizedBody = sanitizeClient(rawContent, clientName);
    const { bestScore, bestMatch } = findBestLibraryMatch(sanitizedBody);

    const isDuplicate = bestScore >= 0.8;
    const isSubstantive = sanitizedBody.split(/\s+/).length >= 10;

    candidates.push({
      id: `sec-${d.id}`,
      sectionId: d.id,
      title: data.title || "Untitled Draft Section",
      body: rawContent,
      sanitizedBody,
      serviceTags: [],
      sourceType: "draft_section",
      author: data.updatedBy || "Proposal Writer",
      authorEmail: data.updatedByEmail || "",
      similarToAnswerId: bestMatch?.id,
      similarToTitle: bestMatch?.title,
      similarityScore: bestScore,
      selected: !isDuplicate && isSubstantive,
      hasReviewedDetails: false,
    });
  });

  // 3. Fetch answered SME tasks
  const smeSnap = await getDocs(collection(db, "opportunities", opportunityId, "sme_tasks"));
  smeSnap.docs.forEach((d) => {
    const data = d.data() as Partial<SmeTask>;
    const rawResponse = (data.response || "").trim();
    if (!rawResponse || (data.status !== "submitted" && data.status !== "accepted")) return;

    const sanitizedBody = sanitizeClient(rawResponse, clientName);
    const { bestScore, bestMatch } = findBestLibraryMatch(sanitizedBody);

    const isDuplicate = bestScore >= 0.8;
    const isSubstantive = sanitizedBody.split(/\s+/).length >= 6;

    candidates.push({
      id: `sme-${d.id}`,
      taskId: d.id,
      title: data.question || "SME Response",
      body: rawResponse,
      sanitizedBody,
      serviceTags: [],
      sourceType: "sme_task",
      author: data.assigneeName || data.assigneeEmail || "Subject Matter Expert",
      authorEmail: data.assigneeEmail || "",
      similarToAnswerId: bestMatch?.id,
      similarToTitle: bestMatch?.title,
      similarityScore: bestScore,
      selected: !isDuplicate && isSubstantive,
      hasReviewedDetails: false,
    });
  });

  return candidates;
}

/**
 * Promotes selected candidates into the Answer Library with 'review_due' status
 */
export async function promoteCandidates(
  opportunityId: string,
  clientName: string,
  candidates: HarvestCandidate[],
  user: UserProfile,
  cadenceDays = 90,
): Promise<string[]> {
  const selected = candidates.filter((c) => c.selected);
  if (selected.length === 0) return [];

  // Check opportunity outcome for initial metadata
  const oppRef = doc(db, "opportunities", opportunityId);
  const oppSnap = await getDoc(oppRef);
  const oppData = oppSnap.exists() ? oppSnap.data() : {};
  const currentOutcome: BidOutcome | null = oppData.outcome || null;
  const isWon = currentOutcome === "won";

  const now = new Date().toISOString();
  const reviewDueDate = calculateReviewDueDate(cadenceDays);
  const createdIds: string[] = [];

  for (const c of selected) {
    const newAnswer: Omit<Answer, "id"> = {
      title: c.title,
      body: c.sanitizedBody,
      serviceTags: c.serviceTags.length > 0 ? c.serviceTags : ["General"],
      status: "review_due", // Harvested answers require human verification
      owner: user.displayName || user.email || "Staff User",
      ownerEmail: user.email,
      lastVerifiedDate: now.slice(0, 10),
      reviewDueDate,
      sourceProposal: clientName,
      sourceOpportunityId: opportunityId,
      sourceOutcome: currentOutcome,
      harvestedFrom: {
        type: c.sourceType,
        id: c.sectionId || c.taskId || c.id,
        opportunityId,
        opportunityTitle: clientName,
        clientName,
        harvestedAt: now,
      },
      wonBidCount: isWon ? 1 : 0,
      wonBidIds: isWon ? [opportunityId] : [],
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, "answers"), sanitizeForFirestore(newAnswer));
    createdIds.push(docRef.id);
  }

  // Update opportunity postMortem record with harvested answer IDs
  if (oppSnap.exists()) {
    const existingHarvested = oppData.postMortem?.harvestedAnswerIds || [];
    const combinedHarvested = Array.from(new Set([...existingHarvested, ...createdIds]));
    await updateDoc(oppRef, {
      "postMortem.harvestedAnswerIds": combinedHarvested,
      updatedAt: now,
    });
  }

  return createdIds;
}

/**
 * Flags an existing Answer Library item for revision following post-mortem loss or client debrief findings.
 */
export async function flagAnswerForReview(
  answerId: string,
  reason: string,
  opportunityId: string,
  user?: { displayName?: string | null; email?: string | null },
): Promise<void> {
  const answerRef = doc(db, "answers", answerId);
  const snap = await getDoc(answerRef);
  if (!snap.exists()) return;

  const now = new Date().toISOString();
  const userName = user?.displayName || user?.email || "Reviewer";

  await updateDoc(answerRef, {
    status: "review_due",
    reviewNotes: `Flagged from Post-Mortem (${opportunityId}) by ${userName}: ${reason}`,
    updatedAt: now,
  });

  // Record on opportunity postMortem
  const oppRef = doc(db, "opportunities", opportunityId);
  const oppSnap = await getDoc(oppRef);
  if (oppSnap.exists()) {
    const oppData = oppSnap.data();
    const existing = oppData.postMortem?.flaggedWeakAnswerIds || [];
    const updated = Array.from(new Set([...existing, answerId]));
    await updateDoc(oppRef, {
      "postMortem.flaggedWeakAnswerIds": updated,
      updatedAt: now,
    });
  }
}
